// Leaf preparation helpers for incremental compile budget checks and grouping.
// Example: one pass trims observed leaves to the allowed budget and builds grouped outputs.
import { countObservedLeaves, partitionRenderLeaves } from '../index-leaves'
import { buildResourceLimitWarning, throwResourceLimitError } from '../resource-limits'
import type { LuisResourceLimits, LuisResourcePolicy } from '../resource-limits'
import type { LuisCompileResult } from '../types'
import type { LuisRenderLeaf } from '../renderer'
import type { IncrementalPreparedLeaves } from './compile-types'

// Build leaf output after expanded-rule and observed-rule budget checks.
export function enforceIncrementalLeafBudgets(
  leaves: LuisRenderLeaf[],
  sourceId: string,
  resourcePolicy: LuisResourcePolicy,
  resourceLimits: LuisResourceLimits,
  resourceWarnings: LuisCompileResult['warnings'],
): LuisRenderLeaf[] {
  let nextLeaves = leaves
  // Check the total rendered-rule budget before incremental rendering.
  if (nextLeaves.length > resourceLimits.maxExpandedRules) {
    const message = `[${sourceId}] Rule budget exceeded (${nextLeaves.length}/${resourceLimits.maxExpandedRules}).`
    if (resourcePolicy === 'fail-closed') {
      throwResourceLimitError(message)
    } else {
      resourceWarnings.push(buildResourceLimitWarning(1, message))
      nextLeaves = nextLeaves.slice(0, resourceLimits.maxExpandedRules)
    }
  }

  const observedLeafCount = countObservedLeaves(nextLeaves)
  // Check the observed-rule budget before runtime payload generation.
  if (observedLeafCount > resourceLimits.maxObservedRules) {
    const message = `[${sourceId}] Observed rule budget exceeded (${observedLeafCount}/${resourceLimits.maxObservedRules}).`
    if (resourcePolicy === 'fail-closed') {
      throwResourceLimitError(message)
    } else {
      resourceWarnings.push(buildResourceLimitWarning(1, message))
      nextLeaves = filterObservedLeaves(nextLeaves, resourceLimits.maxObservedRules)
    }
  }
  // Return leaves after incremental resource-budget enforcement.
  return nextLeaves
}

// Build grouped leaves used by both cold-start and warm incremental paths.
export function prepareIncrementalLeaves(leaves: LuisRenderLeaf[]): IncrementalPreparedLeaves {
  const { regularLeaves, mainRules } = partitionRenderLeaves(leaves)
  const observedLeaves = leaves.filter((leaf) => leaf.observedMode === 'dynamic')
  const initialObservedLeaves = leaves.filter((leaf) => leaf.observedMode === 'initial')
  // Return grouped leaves for CSS, observed runtime, and main rule outputs.
  return { allLeaves: leaves, regularLeaves, observedLeaves, initialObservedLeaves, mainRules }
}

// Build the filtered observed-leaf set so only the allowed count remains active.
function filterObservedLeaves(leaves: LuisRenderLeaf[], maxObservedRules: number): LuisRenderLeaf[] {
  const allowedObserved = new Set<string>()
  let observedSeen = 0
  // Start one observed-leaf allowance scan.
  for (const leaf of leaves) {
    if (leaf.observedMode !== 'none') {
      // Check whether the current observed leaf still fits within the budget.
      if (observedSeen < maxObservedRules) allowedObserved.add(leaf.key)
      observedSeen += 1
    }
  }
  // Return leaves filtered to the allowed observed-rule set.
  return leaves.filter((leaf) => leaf.observedMode === 'none' || allowedObserved.has(leaf.key))
}
